import { useEffect, useState, useCallback } from 'react';
import { readKioskInfoFromStorage } from '@/services/kioskInfoCache';
import type { DomainType } from '@/hooks/useDomainType';
import { getHealthUrl } from '@/config/apiConfig';

const REACHABILITY_INTERVAL_MS = 30 * 1000;
const PING_TIMEOUT_MS = 8000;

/**
 * True when this client app should show fullscreen "connecting" overlay.
 * - Any registered client domain: kiosk, mobile (from API or cached /api/kiosk/info).
 * - Browser offline (DevTools / airplane) OR online but backend health check fails.
 *
 * Reachability uses GET {VITE_API_URL}/health (same backend as the app), not a third-party
 * URL like gstatic — otherwise the overlay can stick after reconnect when Google is blocked
 * but the API is reachable.
 */
function isClientDomain(domainType: DomainType | 'loading', cachedType: string | undefined): boolean {
  if (domainType === 'kiosk' || domainType === 'mobile') return true;
  if (domainType === 'loading' && (cachedType === 'kiosk' || cachedType === 'mobile')) return true;
  return false;
}

export function useKioskOfflineScreen(domainType: DomainType | 'loading'): {
  show: boolean;
  retry: () => Promise<void>;
  retryBusy: boolean;
} {
  const [browserOffline, setBrowserOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );
  const [pingUnreachable, setPingUnreachable] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const cachedType = host ? readKioskInfoFromStorage(host)?.type : undefined;
  const useOfflineOverlay = isClientDomain(domainType, cachedType);

  const runPing = useCallback(async () => {
    if (!useOfflineOverlay) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setPingUnreachable(false);
      return;
    }
    const controller = new AbortController();
    const t = window.setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    try {
      const res = await fetch(`${getHealthUrl()}?kiosk=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
        credentials: 'omit',
      });
      if (!res.ok) {
        throw new Error(`health ${res.status}`);
      }
      setPingUnreachable(false);
    } catch {
      setPingUnreachable(true);
    } finally {
      window.clearTimeout(t);
    }
  }, [useOfflineOverlay]);

  useEffect(() => {
    const sync = () =>
      setBrowserOffline(typeof navigator !== 'undefined' && !navigator.onLine);
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  useEffect(() => {
    if (!useOfflineOverlay) {
      setPingUnreachable(false);
      return;
    }
    if (browserOffline) {
      setPingUnreachable(false);
      return;
    }

    void runPing();
    const intervalId = window.setInterval(() => {
      void runPing();
    }, REACHABILITY_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [useOfflineOverlay, browserOffline, runPing]);

  const retry = useCallback(async () => {
    if (!useOfflineOverlay) return;
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    setBrowserOffline(offline);
    if (offline) return;
    setRetryBusy(true);
    try {
      await runPing();
    } finally {
      setRetryBusy(false);
    }
  }, [useOfflineOverlay, runPing]);

  if (!useOfflineOverlay) {
    return { show: false, retry, retryBusy: false };
  }

  return {
    show: browserOffline || pingUnreachable,
    retry,
    retryBusy,
  };
}
